import type {
  GetApiShipmentsData,
  ShipmentOrigin,
  ShipmentStatus,
} from '@/api';
import type { TableSort } from '@/ui';

import { FINISHED_SHIPMENT_STATUSES } from '../../../lib/shipmentStatus';
import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние истории отгрузок - в search params маршрута, как у списка отгрузок:
 * в URL только то, что отличается от умолчания. Период - по плановой дате
 * (у отгрузки по заказу 1С - дата отгрузки заказа), обе границы включительно.
 */

export const PAGE_SIZE = 20;

/** «Все закрытые» (по умолчанию) или один закрытый статус. */
export type HistoryStatusFilter = 'finished' | ShipmentStatus;

/** Поля, по которым бэкенд разрешает сортировать (SearchShipmentsQuery.SortFields). */
export const SORT_FIELDS = ['plannedDate', 'number', 'createdAt'] as const;

const DEFAULT_SORT: TableSort = { field: 'plannedDate', dir: 'desc' };

export interface HistorySearch {
  search?: string;
  status?: HistoryStatusFilter;
  from?: string;
  to?: string;
  origin?: ShipmentOrigin;
  discrepancies?: boolean;
  page?: number;
  sort?: string;
}

export interface ResolvedHistorySearch {
  search: string;
  status: HistoryStatusFilter;
  from: string;
  to: string;
  origin: ShipmentOrigin | 'all';
  discrepancies: boolean;
  page: number;
  sort: TableSort;
}

const isStatusFilter = (value: unknown): value is HistoryStatusFilter =>
  value === 'finished' ||
  (FINISHED_SHIPMENT_STATUSES as ReadonlyArray<unknown>).includes(value);

const isOrigin = (value: unknown): value is ShipmentOrigin =>
  value === 'Culture' || value === 'Onec';

const isDay = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

export const validateHistorySearch = (
  raw: Record<string, unknown>,
): HistorySearch => {
  const result: HistorySearch = {};

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isStatusFilter(raw.status) && raw.status !== 'finished')
    result.status = raw.status;

  if (isDay(raw.from)) result.from = raw.from;
  if (isDay(raw.to)) result.to = raw.to;
  if (isOrigin(raw.origin)) result.origin = raw.origin;
  if (raw.discrepancies === true || raw.discrepancies === 'true')
    result.discrepancies = true;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  if (sort && !isSameSort(sort, DEFAULT_SORT)) result.sort = formatSort(sort);

  return result;
};

export const resolveHistorySearch = (
  search: HistorySearch,
): ResolvedHistorySearch => ({
  search: search.search ?? '',
  status: search.status ?? 'finished',
  from: search.from ?? '',
  to: search.to ?? '',
  origin: search.origin ?? 'all',
  discrepancies: search.discrepancies ?? false,
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

/** День после данного - бэкенд исключает правую границу периода. */
const nextDay = (day: string): string => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

export const toHistoryQuery = (
  resolved: ResolvedHistorySearch,
): NonNullable<GetApiShipmentsData['query']> => {
  const query: NonNullable<GetApiShipmentsData['query']> = {
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
    Statuses:
      resolved.status === 'finished'
        ? [...FINISHED_SHIPMENT_STATUSES]
        : [resolved.status],
  };

  if (resolved.search !== '') query.Search = resolved.search;
  if (resolved.from !== '') query.PlannedDateFrom = resolved.from;
  if (resolved.to !== '') query.PlannedDateTo = nextDay(resolved.to);
  if (resolved.origin !== 'all') query.Origin = resolved.origin;
  if (resolved.discrepancies) query.HasOnecDiscrepancies = true;

  return query;
};
